import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [relatedComics, setRelatedComics] = useState<Comic[]>([]);
  const [selectedTab, setSelectedTab] = useState<'chapters' | 'info'>('chapters');
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    const found = comics.find((c) => c.id === id);
    setComic(found || null);

    if (found) {
      const related = comics
        .filter((c) => c.id !== id && c.genres.some((g) => found.genres.includes(g)))
        .slice(0, 4);
      setRelatedComics(related);
    }
  }, [id]);

  const getCategoryName = (slug: string) => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : slug;
  };

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-book-2-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-heading font-bold text-foreground-800">漫画不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm mt-4 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm py-4" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <Link to={`/category/${comic.genres[0]}`} className="text-foreground-500 hover:text-primary-500 transition-colors">
                {getCategoryName(comic.genres[0])}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
              <span className="text-foreground-800 font-medium truncate">{comic.title}</span>
            </nav>
          </div>
        </div>

        {/* Comic Header */}
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-3">
                  <span>作者：{comic.author}</span>
                  <span className="text-foreground-300">|</span>
                  <span>画师：{comic.artist}</span>
                  <span className="text-foreground-300">|</span>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
                    comic.status === 'ongoing'
                      ? 'bg-primary-50 text-primary-600'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-5 mb-4">
                  <div className="flex items-center gap-1">
                    <span className="text-2xl font-bold text-accent-500">{comic.rating}</span>
                    <span className="text-sm text-foreground-500">分</span>
                    <div className="flex ml-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`ri-star-fill text-sm ${
                            star <= Math.round(comic.rating / 2)
                              ? 'text-accent-500'
                              : 'text-background-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {(comic.views / 10000).toFixed(0)}万人气
                  </span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-file-list-2-line mr-1"></i>
                    共{comic.chapters.length}话
                  </span>
                </div>

                {/* Genres */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {comic.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category/${genre}`}
                      className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      {getCategoryName(genre)}
                    </Link>
                  ))}
                </div>

                {/* Description */}
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    开始阅读 第1话
                  </Link>
                  <button
                    onClick={() => setLiked(!liked)}
                    className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-200 ${
                      liked
                        ? 'bg-primary-50 border-primary-200 text-primary-500'
                        : 'bg-background-100 border-background-200/70 text-foreground-500 hover:border-primary-200 hover:text-primary-500'
                    }`}
                  >
                    <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs: Chapters & Info */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-8 w-fit">
            <button
              onClick={() => setSelectedTab('chapters')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                selectedTab === 'chapters'
                  ? 'bg-background-50 text-foreground-900'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              章节列表 ({comic.chapters.length})
            </button>
            <button
              onClick={() => setSelectedTab('info')}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                selectedTab === 'info'
                  ? 'bg-background-50 text-foreground-900'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              作品信息
            </button>
          </div>

          {selectedTab === 'chapters' && (
            <div className="bg-background-100/50 rounded-lg border border-background-200/70 p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {comic.chapters.map((chapter) => (
                  <a
                    key={chapter.id}
                    href="#"
                    className="flex items-center justify-between p-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-200 group cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 text-xs font-medium flex-shrink-0">
                        {chapter.number}
                      </span>
                      <span className="text-sm text-foreground-700 font-medium truncate group-hover:text-primary-500 transition-colors">
                        第{chapter.number}话 {chapter.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-500 flex-shrink-0 ml-3">{chapter.date}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {selectedTab === 'info' && (
            <div className="bg-background-100/50 rounded-lg border border-background-200/70 p-6">
              <dl className="space-y-4">
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">作品名</dt>
                  <dd className="text-sm text-foreground-800">{comic.title}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">作者</dt>
                  <dd className="text-sm text-foreground-800">{comic.author}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">画师</dt>
                  <dd className="text-sm text-foreground-800">{comic.artist}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">状态</dt>
                  <dd className="text-sm text-foreground-800">{comic.status === 'ongoing' ? '连载中' : '已完结'}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">评分</dt>
                  <dd className="text-sm text-foreground-800">{comic.rating}分</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">人气</dt>
                  <dd className="text-sm text-foreground-800">{(comic.views / 10000).toFixed(0)}万</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">更新</dt>
                  <dd className="text-sm text-foreground-800">{comic.updatedAt}</dd>
                </div>
                <div className="flex gap-4">
                  <dt className="text-sm font-medium text-foreground-500 w-20 flex-shrink-0">简介</dt>
                  <dd className="text-sm text-foreground-600 leading-relaxed">{comic.description}</dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <div className="bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">相关推荐</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((related) => (
                  <Link
                    key={related.id}
                    to={`/comic/${related.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200/70 mb-3">
                      <img
                        src={related.cover}
                        alt={related.title}
                        title={`${related.title} - 相关推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <div className="flex items-center gap-1 text-white/90 text-xs">
                          <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                          <span>{related.rating}</span>
                        </div>
                      </div>
                    </div>
                    <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                      {related.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}